import { computed, ref, toValue, type MaybeRefOrGetter, type WritableComputedRef } from "vue";
import { useRoute } from "vue-router";

import { useQueryWriter } from "@/composables/shared/queries/useFilterFields";

/** A header (subset) that can declare the backend ordering field for its column. */
type TSortableHeader = { key: string; sortKey?: string };

/** A single Vuetify data-table sort entry. */
export type TSortItem = { key: string; order: "asc" | "desc" };

/** Default query-param name used to persist table ordering in the URL. */
export const ORDERING_QUERY_KEY = "ordering";

/**
 * Parses an `ordering` query string into `sortBy` entries.
 *
 * The wire format mirrors DRF ordering: a comma-separated list of fields, each optionally
 * prefixed with `-` for descending order (e.g. `"-last_name,phase"`). The tokens are the
 * backend `ordering` fields when a header map is used (see `useUrlSort`), otherwise the
 * column keys directly.
 */
export function parseOrdering(raw: unknown): TSortItem[] {
  let str = "";
  if (typeof raw === "string") {
    str = raw;
  } else if (Array.isArray(raw) && typeof raw[0] === "string") {
    str = raw[0];
  }

  return str
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean)
    .map((token) =>
      token.startsWith("-")
        ? { key: token.slice(1), order: "desc" as const }
        : { key: token, order: "asc" as const }
    );
}

/**
 * Rewrites `sortBy` column keys to the backend `ordering` field declared on each header
 * via `sortKey`, leaving the order untouched. Columns without a `sortKey` keep their key.
 *
 * Apply this when building the filters sent to the API — the data table's sort arrow keeps
 * the column key (for highlighting), while the request carries the backend field. This is
 * the header-level alternative to a service `fieldMap`.
 */
export function applySortKeys(
  sortBy: readonly TSortItem[],
  headers: readonly TSortableHeader[]
): TSortItem[] {
  if (sortBy.length === 0) return [...sortBy];

  const fieldByKey = new Map(
    headers.filter((header) => !!header.sortKey).map((header) => [header.key, header.sortKey!])
  );
  if (fieldByKey.size === 0) return [...sortBy];

  return sortBy.map((item) => {
    const field = fieldByKey.get(item.key);
    return field ? { ...item, key: field } : item;
  });
}

/**
 * Inverse of `applySortKeys`: rewrites backend `ordering` fields back to their column keys,
 * so a field read from the URL/request highlights the right column's sort arrow. A field
 * with no matching header (`sortKey ?? key`) is left as-is.
 */
export function reverseSortKeys(
  sortBy: readonly TSortItem[],
  headers: readonly TSortableHeader[]
): TSortItem[] {
  if (sortBy.length === 0) return [...sortBy];

  const keyByField = new Map(headers.map((header) => [header.sortKey ?? header.key, header.key]));

  return sortBy.map((item) => {
    const key = keyByField.get(item.key);
    return key ? { ...item, key } : item;
  });
}

/** Serializes Vuetify `sortBy` entries into an `ordering` query string. */
export function serializeOrdering(sortBy: readonly TSortItem[] | undefined): string {
  return (sortBy ?? [])
    .filter((item) => !!item?.key)
    .map((item) => (item.order === "desc" ? "-" : "") + item.key)
    .join(",");
}

/** The `v-model:options` shape our data tables speak; only `sortBy` is ever used. */
export type TTableOptions = { sortBy: TSortItem[] };

/**
 * Wraps a `sortBy` model in the `{ sortBy }` shape the data tables' `v-model:options`
 * expects, forwarding writes back to the source ref.
 *
 * The tables emit their full options object on every internal change, so call sites would
 * otherwise each hand-roll the same two-line adapter around whatever owns the sort
 * (`useUrlSort`, a `defineModel`, or plain local state).
 */
export function toTableOptions(
  sortBy: WritableComputedRef<TSortItem[]> | { value: TSortItem[] }
): WritableComputedRef<TTableOptions> {
  return computed<TTableOptions>({
    get: () => ({ sortBy: sortBy.value }),
    set: (value) => {
      sortBy.value = value?.sortBy ?? [];
    },
  });
}

/**
 * Two-way binds a data table's `sortBy` to the `ordering` URL query param, so column
 * sorting is shareable, bookmarkable and survives a reload.
 *
 * The returned `sortBy` is keyed by **column key** (what Vuetify's sort arrow needs). When
 * `headers` are supplied, the **backend field** (`sortKey ?? key`) is what gets written to
 * the URL — e.g. a `patient` column with `sortKey: "last_name"` produces `?ordering=last_name`
 * while the arrow stays on the `patient` column. Without `headers`, the column key is used
 * verbatim (and the service `fieldMap` handles any key→field translation for the request).
 *
 * When `listenOff` is set the sort is kept in local component state instead of the URL,
 * so nested/embedded tables (dialogs, cards) can still sort without polluting the route.
 *
 * With `singleSort`, only the first entry is ever kept — pair it with the table's
 * `:multi-sort="false"` so a new column *replaces* the previous sort. Clamping here (rather
 * than only at the table) also collapses a stale multi-field `?ordering=` from an older
 * shared link, so the URL and the request can never carry more than one column.
 *
 * @param queryKey Query-param name to read/write. Defaults to `ordering`.
 * @param listenOff When true, keep sort in local state rather than the URL.
 * @param headers Table headers, used to translate between column keys and backend fields.
 * @param singleSort When true, clamp the sort to a single column in both directions.
 */
export const useUrlSort = (
  queryKey: string = ORDERING_QUERY_KEY,
  listenOff: boolean = false,
  headers?: MaybeRefOrGetter<readonly TSortableHeader[]>,
  singleSort: boolean = false
) => {
  const route = useRoute();
  const writeQuery = useQueryWriter();

  const localSort = ref<TSortItem[]>([]);

  const clamp = (items: TSortItem[]) => (singleSort ? items.slice(0, 1) : items);

  const sortBy = computed<TSortItem[]>({
    get: () => {
      if (listenOff) return clamp(localSort.value);
      // URL holds backend fields; map them back to column keys for the sort arrow.
      const fromUrl = parseOrdering(route.query[queryKey]);
      const hs = headers ? toValue(headers) : undefined;
      return clamp(hs ? reverseSortKeys(fromUrl, hs) : fromUrl);
    },
    set: (input) => {
      const next = clamp(input ?? []);

      if (listenOff) {
        localSort.value = next;
        return;
      }

      // `next` is column-key based (from the data table); persist the backend field.
      const hs = headers ? toValue(headers) : undefined;
      const ordering = serializeOrdering(hs ? applySortKeys(next, hs) : next);
      const current = typeof route.query[queryKey] === "string" ? route.query[queryKey] : "";
      // Skip redundant navigations: the data table re-emits its options on every
      // internal change, most of which don't touch the ordering.
      if (ordering === (current ?? "")) return;

      void writeQuery({ [queryKey]: ordering || null });
    },
  });

  return { sortBy };
};
